import { useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { type UserProfile } from '../onboardingStore';

interface QuickStartPickerProps {
  onSelect: (profile: UserProfile) => void;
}

const PROFILES: {
  id: UserProfile;
  title: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    id: 'beginner',
    title: 'New to EnergyPlus',
    description: "I'm learning building energy modeling",
    icon: (
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
      </svg>
    ),
  },
  {
    id: 'expert',
    title: 'EnergyPlus Pro',
    description: 'I know EnergyPlus, show me the web editor',
    icon: (
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ),
  },
  {
    id: 'exploring',
    title: 'Just Exploring',
    description: "I'll figure things out on my own",
    icon: (
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
      </svg>
    ),
  },
];

/**
 * Three-card user-type selector with keyboard navigation.
 */
export function QuickStartPicker({ onSelect }: QuickStartPickerProps) {
  const cardRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      let nextIndex: number | null = null;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        nextIndex = (index + 1) % PROFILES.length;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        nextIndex = (index - 1 + PROFILES.length) % PROFILES.length;
      }

      if (nextIndex !== null) {
        cardRefs.current[nextIndex]?.focus();
      }
    },
    []
  );

  return (
    <div className="space-y-4">
      <h3 className="text-center text-lg font-semibold">How would you like to get started?</h3>
      <div className="grid gap-3 sm:grid-cols-3">
        {PROFILES.map((profile, i) => (
          <button
            key={profile.id}
            ref={(el) => {
              cardRefs.current[i] = el;
            }}
            onClick={() => { onSelect(profile.id); }}
            onKeyDown={(e) => { handleKeyDown(e, i); }}
            className={cn(
              'flex flex-col items-center gap-3 rounded-lg border bg-card p-5 text-center',
              'transition-all hover:shadow-md hover:border-primary',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            )}
          >
            <div className="text-primary">{profile.icon}</div>
            <div>
              <div className="font-semibold">{profile.title}</div>
              <p className="mt-1 text-sm text-muted-foreground">{profile.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
